import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Table from '../../../core/form/Table';
import AssetsStatusTag from '../../../core/layout/AssetsStatusTag';
import AudioPreview from '../../../core/layout/AudioPreview';
import Button from '../../../core/form/Button';
import { startCase } from 'lodash';
import ImagePreview from '../../../core/layout/ImagePreview';
import TextPreview from '../../../core/layout/TextPreview';
import Tabs from '../../../core/layout/Tabs';
import ItemFormContainer from '../../item/components/ItemFormContainer';
import DraftItemAssetsListContainer from './DraftItemAssetsListContainer';
import Separator from '../../../core/layout/Separator';
import VideoPreview, { Modes as VideoPreviewModes } from '../../../core/layout/VideoPreview';
import NativeSpeechSlice, { Modes as NativeSpeechSliceModes } from '../../item/components/inputs/NativeSpeechSlice';

const DraftItemList = (props) => {
  const tableContainerRef = useRef();
  const [isScrollNeeded, setIsScrollNeeded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const updateFixBaseColumns = useCallback(() => {
    setIsScrollNeeded(
      tableContainerRef.current &&
        tableContainerRef.current.querySelector('.ant-table-wrapper').clientWidth <
          [...tableContainerRef.current.querySelectorAll('th:not(.ant-table-fixed-columns-in-body)')].reduce(
            (acc, el) => {
              acc += el.clientWidth;
              return acc;
            },
            0
          )
    );
  }, []);

  useEffect(updateFixBaseColumns, [props.extraColumns.length]);

  useEffect(() => {
    let resizeTimeout;
    const listener = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateFixBaseColumns, 100);
    };
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, [updateFixBaseColumns]);

  const { extraColumns, onChangeAssetStatus, onDelete, onPublish } = props;

  const extraColumnsMemoized = useMemo(
    () =>
      extraColumns.map((column) => {
        if (['audio', 'postPhraseAudio', 'titleAudio'].some((c) => c === column)) {
          return {
            label: startCase(column.replace(/([A-Z])/g, ' $1')),
            path: column,
            width: '70px',
            align: 'center',
            render: (cell) => (cell ? <AudioPreview src={cell} /> : null),
          };
        }
        if (['character.name', 'grammar.name', 'draftCreationReason', 'title'].some((c) => c === column)) {
          return {
            label: startCase(column.replace(/([A-Z])/g, ' $1')),
            path: column,
            width: '120px',
          };
        }
        if (['translation', 'spanishTranslation', 'postPhrase'].some((c) => c === column)) {
          return {
            label: startCase(column.replace(/([A-Z])/g, ' $1')),
            path: column,
            width: '160px',
            render: (cell) => cell && <TextPreview text={cell} maxCharacters={150} />,
          };
        }
        if (['audioAssetsStatus', 'imageAssetsStatus', 'videoAssetsStatus'].some((c) => c === column)) {
          return {
            label: {
              audioAssetsStatus: 'Status Audio',
              imageAssetsStatus: 'Status Image',
              videoAssetsStatus: 'Status Video',
            }[column],
            path: column,
            align: 'center',
            width: '120px',
            render: (cell, row) =>
              cell ? (
                <div>
                  <AssetsStatusTag status={cell} />
                  {cell === 'TO_DO' && (
                    <Button
                      icon="right-circle"
                      tooltip="Send to In Progress"
                      onClick={() =>
                        onChangeAssetStatus(
                          row.id,
                          {
                            audioAssetsStatus: 'audio',
                            imageAssetsStatus: 'image',
                            videoAssetsStatus: 'video',
                          }[column]
                        )
                      }
                    />
                  )}
                </div>
              ) : (
                'N/A'
              ),
          };
        }
        if (column === 'image' || column === 'postPhraseImage') {
          return {
            label: startCase(column.replace(/([A-Z])/g, ' $1')),
            path: column,
            width: '190px',
            align: 'center',
            render: (cell, row) => (
              <>
                {cell && <ImagePreview src={cell} renderType="image" />}
                {(row.type.requiredAssetsTypes || []).some(
                  (assetType) => assetType === 'ALL_ANSWERS_IMAGE' || assetType === 'ONLY_CORRECT_ANSWER_IMAGE'
                ) && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: row.type.requiredAssetsTypes.some(
                        (assetType) => assetType === 'ONLY_CORRECT_ANSWER_IMAGE'
                      )
                        ? '170'
                        : '80px 80px',
                      marginTop: cell ? 10 : 0,
                      gridGap: 5,
                      justifyContent: 'center',
                    }}
                  >
                    {row.answers
                      .filter((a) =>
                        row.type.requiredAssetsTypes.some((assetType) => assetType === 'ONLY_CORRECT_ANSWER_IMAGE')
                          ? a.correct
                          : true
                      )
                      .map((a) => (
                        <ImagePreview key={`answer-imagem-${a.id}`} src={a.image} renderType="image" size="sm" />
                      ))}
                  </div>
                )}
              </>
            ),
          };
        }
        if (column === 'answers') {
          return {
            label: 'Answers',
            path: column,
            width: '150px',
            render: (cell) => {
              if (cell && cell.length) {
                return (
                  <>
                    {cell.map((a, index) => (
                      <div key={`answer-audio-${a.id}`} style={{ display: 'flex', marginBottom: 5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', minWidth: 30 }}>
                          {!!a.audio && <AudioPreview src={a.audio} size="sm" />}
                        </div>
                        <div>
                          <span style={{ ...(a.correct && { fontWeight: 500, color: '#58c430' }) }}>
                            {index + 1} - {a.text}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                );
              }
              return null;
            },
          };
        }

        if (column === 'videoExternalLink') {
          return {
            label: 'Vídeo',
            path: column,
            width: '70px',
            render: (cell, row) => {
              if (cell) {
                return (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <VideoPreview src={cell} id={cell} mode={VideoPreviewModes.BUTTON_MODAL} />
                  </div>
                );
              } else if (row.videoLink) {
                return (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <VideoPreview src={row.videoLink} id={row.videoLink} mode={VideoPreviewModes.BUTTON_MODAL} />
                  </div>
                );
              }
            },
          };
        }

        return {
          label: column,
          path: column,
          width: '100px',
        };
      }),
    [extraColumns, onChangeAssetStatus]
  );

  const baseColumnsMemoized = useMemo(() => {
    if (props.type === 'course') {
      return [
        {
          label: 'Type',
          path: 'type.name',
          width: '120px',
        },
        {
          label: 'Course',
          path: 'course.name',
          width: '70px',
        },
        {
          label: 'Module',
          path: 'module.name',
          width: '160px',
        },
        {
          label: 'Unit / Mastery',
          path: 'scope',
          width: '120px',
          render: (scope, row) =>
            ({ UNIT: row.unit?.name, MASTERY_TEST: `${row.masteryTest?.modulePercentageToActive}%` }[scope]),
        },
        {
          label: 'Order',
          path: 'associativeItem.order',
          width: '55px',
        },
        {
          label: 'Group',
          path: 'associativeItem.group',
          width: '80px',
          render: (cell) => ['', 'Default', 'First review', 'Second review'][cell],
        },
        {
          label: 'Text',
          path: 'text',
          width: '200px',
          render: (cell, row) => {
            if (
              ['PRESENTATION', 'PRONUNCIATION', 'SPEECH_PRACTICE', 'VOCABULARY', 'VOCABULARY_ACADEMIC'].includes(
                row.type.key
              )
            ) {
              if (['VOCABULARY', 'VOCABULARY_ACADEMIC'].includes(row.type.key)) {
                if (row.postPhraseAudio) {
                  return (
                    <NativeSpeechSlice values={row} mode={NativeSpeechSliceModes.LINK} audioPath="postPhraseAudio" />
                  );
                } else {
                  return <TextPreview text={row.postPhrase} maxCharacters={150} />;
                }
              } else if (row.audio) {
                return <NativeSpeechSlice values={row} mode={NativeSpeechSliceModes.LINK} />;
              } else {
                return <TextPreview text={cell} maxCharacters={150} />;
              }
            } else {
              return <TextPreview text={cell} maxCharacters={150} />;
            }
          },
        },
      ];
    }
    if (props.type === 'placement') {
      return [
        {
          label: 'Type',
          path: 'type.name',
          width: '120px',
        },
        {
          label: 'Level',
          path: 'placementTestLevel.level',
          width: '70px',
        },
        {
          label: 'Grammar',
          path: 'grammar.name',
          width: '160px',
        },
        {
          label: 'Text',
          path: 'text',
          width: '200px',
          render: (cell) => <TextPreview text={cell} maxCharacters={150} />,
        },
      ];
    }
    if (props.type === 'certification') {
      return [
        {
          label: 'Type',
          path: 'type.name',
          width: '120px',
        },
        {
          label: 'Course',
          path: 'course.name',
          width: '70px',
        },
        {
          label: 'Ability',
          path: 'certificationTestCourseAbility.ability',
          width: '70px',
          render: (cell) => cell && startCase(cell.toLowerCase()),
        },
        {
          label: 'Grammar',
          path: 'grammar.name',
          width: '160px',
        },
        {
          label: 'Text',
          path: 'text',
          width: '200px',
          render: (cell) => <TextPreview text={cell} maxCharacters={150} />,
        },
      ];
    }
    return [];
  }, [props.type]);

  const tableColumnsMemoized = useMemo(
    () => [
      ...baseColumnsMemoized,
      ...extraColumnsMemoized,
      {
        label: 'Actions',
        path: 'action',
        width: '85px',
        render: (cell, row) => {
          return (
            <div>
              <Button icon="delete" tooltip="Remove" onClick={() => onDelete(row)} />{' '}
              <Button icon="cloud-upload" tooltip="Publish" onClick={() => onPublish(row)} />
            </div>
          );
        },
      },
    ],
    [baseColumnsMemoized, extraColumnsMemoized, onDelete, onPublish]
  );

  const scrollConfig = useMemo(() => {
    if (isScrollNeeded) {
      return {
        x: [...baseColumnsMemoized, ...extraColumnsMemoized].reduce(
          (acc, c) => (acc + c.width ? parseInt(c.width.replaceAll(/[a-z]/g, '')) : 100),
          0
        ),
      };
    }
    return {};
  }, [isScrollNeeded, baseColumnsMemoized, extraColumnsMemoized]);

  const handleToggleRows = useCallback(() => {
    const buttons = tableContainerRef.current.getElementsByClassName(
      expanded ? 'ant-table-row-expanded' : 'ant-table-row-collapsed'
    );
    for (let button of buttons) {
      button.click();
    }
    setExpanded(!expanded);
  }, [expanded]);

  return (
    <div ref={tableContainerRef}>
      <Button
        label={expanded ? 'Collapse all' : 'Expand all'}
        onClick={handleToggleRows}
        icon={expanded ? 'minus-square' : 'plus-square'}
      />
      <Separator size="xs" />
      <Table
        pagination={props.pagination}
        onChange={props.onChange}
        selectedRows={props.selectedItems}
        onSelectRows={props.onSelectItem}
        scroll={scrollConfig}
        fixedHorizontalScroll={true}
        columns={tableColumnsMemoized}
        fetching={props.fetching}
        rows={props.items}
        expandedRowKeys={expanded}
        expandableComponent={(row) => (
          <Tabs
            storeInUrl={false}
            tabs={[
              {
                key: 'uploads',
                title: 'Uploads',
                content: <DraftItemAssetsListContainer item={row} />,
              },
              {
                key: 'item-form',
                title: 'Item Form',
                content: (
                  <div style={{ padding: '0px 10px' }}>
                    <ItemFormContainer
                      itemId={row.id}
                      itemsTypeUrl={
                        {
                          UNIT: `unit-types/${row.unit?.type?.id}/item-types`,
                          MASTERY_TEST: '/item-types?allowedForMasteryTest=true',
                          PLACEMENT: '/item-types?allowedForPlacementTest=true',
                          CERTIFICATION_TEST: `/item-types?allowedForCertificationTest=${row.certificationTestCourseAbility?.ability}`,
                        }[row.scope]
                      }
                      endpointUrl={
                        {
                          UNIT: `units/${row.unit?.id}/items`,
                          MASTERY_TEST: `/mastery-tests/${row.masteryTest?.id}/items`,
                          PLACEMENT: `/grammar-placement-test-levels/${row.grammarPlacementTestLevel?.id}/items`,
                          CERTIFICATION_TEST: `/certification-test-course-ability/${row.certificationTestCourseAbility?.id}/items`,
                        }[row.scope]
                      }
                      order={row.associativeItem?.order ?? null}
                      disabled={row.status !== 'DRAFT' || props.disabled}
                      showPostPhrase={row.scope === 'UNIT' && row.unit.type.name.toLowerCase() === 'vocabulary'}
                      isTestItem={row.scope !== 'UNIT'}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      />
    </div>
  );
};

DraftItemList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      status: PropTypes.string,
    })
  ).isRequired,
  fetching: PropTypes.bool.isRequired,
  pagination: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    pageSize: PropTypes.number,
  }).isRequired,
  extraColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onChangeAssetStatus: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['course', 'placement', 'certification']).isRequired,
};

export default DraftItemList;
